import React from 'react';

interface SearchFilterBarProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  onClearSearch?: () => void;
  onOpenFilters: () => void;
  hasActiveFilters?: boolean;
  activeFiltersCount?: number;
  // optional icons (URLs/imports) for the filter and dropdown visuals
  tuneIcon?: string;
}

const SearchFilterBar: React.FC<SearchFilterBarProps> = ({
  searchTerm,
  onSearchChange,
  onClearSearch,
  onOpenFilters,
  hasActiveFilters = false,
  activeFiltersCount = 0,
  tuneIcon,
}) => {
  return (
    <div className="flex w-full min-w-0 flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      {/* responsive widths: full on very small screens, compact on larger screens */}
  <div className="relative w-full min-w-0 sm:max-w-xl lg:max-w-2xl">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <svg className="h-5 w-5 text-purple-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        <input
          type="text"
          placeholder="Search Employee"
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          className="block w-full pl-10 pr-12 border border-purple-800 rounded-full leading-5 bg-purple-50 text-purple-800 placeholder-purple-800 focus:outline-none focus:placeholder-purple-500  focus:ring-purple-800 focus:border-purple-800"
          style={{ paddingTop: '14px', paddingBottom: '14px' }}
        />

        {searchTerm && (
          <button
            onClick={() => { if (onClearSearch) { onClearSearch(); } }}
            className="absolute inset-y-0 right-8 pr-3 flex items-center"
            aria-label="Clear search"
          >
            <svg className="h-5 w-5 text-purple-400 hover:text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}

        <button
          onClick={onOpenFilters}
          className={`absolute inset-y-0 right-0 pr-3 flex items-center ${hasActiveFilters ? 'text-purple-600' : 'text-gray-400'} hover:text-purple-600`}
          aria-label="Open filters"
        >
          {tuneIcon ? <img src={tuneIcon} alt="Filter" className="h-6 w-6" /> : (
            <svg className="h-5 w-5 text-current" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L15 14.414V19a1 1 0 01-.553.894l-4 2A1 1 0 019 21v-6.586L3.293 6.707A1 1 0 013 6V4z"/></svg>
          )}
        </button>
      </div>

      <div className="flex max-w-full flex-wrap items-center gap-2 text-sm text-gray-600 sm:justify-end">
        {searchTerm && (
          <span className="max-w-full truncate bg-purple-100 text-purple-800 px-2 py-1 rounded-md">
            Search: "{searchTerm}"
          </span>
        )}
        {(hasActiveFilters || activeFiltersCount > 0) && (
          <div className="flex items-center space-x-2">
            <span className="inline-flex items-center space-x-2 bg-blue-100 text-blue-800 px-2 py-1 rounded-md">
              <span>{activeFiltersCount} filter{activeFiltersCount !== 1 ? 's' : ''} active</span>

              <button
                type="button"
                onClick={() => {
                  // emit a global event so the parent can listen and clear filters
                  window.dispatchEvent(new CustomEvent('clearFilters'));
                }}
                className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-transparent text-blue-800 hover:text-blue-600 focus:outline-none"
                aria-label="Clear all filters"
                title="Clear all filters"
              >
                <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchFilterBar;
